import { Alert, Box, Button, Grid } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { acceptInvoiceMission, rejectInvoiceMission } from 'src/shared/services/invoiceMissionService';
import { convertSendingTypeIntToString } from './invoiceMissionSendingTypeConverter';

type InvoiceMissionApproveButtonProps = {
  invoiceMissionId: number;
  sendingType: InvoiceSendingTypeIntegerEnum;
  refetchMission: (refetch: boolean) => void;
};

type FormValues = {
  message: string;
};

export const InvoiceMissionApproveButtons = (props: InvoiceMissionApproveButtonProps) => {
  const { invoiceMissionId, sendingType, refetchMission } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [, setLoading] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [confirmApproveDialog, setConfirmApproveDialog] = useState<boolean>(false);

  const maxChar = 50;
  const requiredMessage = t('common:validationMessages.required');

  const schema: yup.Schema<FormValues> = yup.object().shape({
    message: yup
      .string()
      .max(maxChar, t('common:validationMessages.maxLength', { count: maxChar }))
      .required(requiredMessage),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      message: '',
    },
  });

  const { handleSubmit, reset } = formMethods;

  const setSendingName = () => {
    if (sendingType === InvoiceSendingTypeIntegerEnum.Main) {
      return t('invoice:invoiceMissionDetailTabs.mainSending');
    }
    if (sendingType === InvoiceSendingTypeIntegerEnum.Reminder1) {
      return t('invoice:invoiceMissionDetailTabs.reminderOne');
    }
    if (sendingType === InvoiceSendingTypeIntegerEnum.Reminder2) {
      return t('invoice:invoiceMissionDetailTabs.reminderTwo');
    }
    return '';
  };

  const approveMission = async () => {
    setLoading(true);

    const convertedSendingType = convertSendingTypeIntToString(sendingType);
    try {
      await acceptInvoiceMission(invoiceMissionId, convertedSendingType);

      if (isMounted()) {
        toast.success(<>{t('invoice:invoiceMissionPage.approveMissionSuccess')}</>);
        setConfirmApproveDialog(false);
        refetchMission(true);
      }
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('invoice:invoiceMissionPage.approveMissionError')}</>);
    } finally {
      setLoading(false);
    }
  };

  const rejectMission = async (data: FormValues) => {
    setLoading(true);
    const convertedSendingType = convertSendingTypeIntToString(sendingType);
    try {
      await rejectInvoiceMission(invoiceMissionId, convertedSendingType, data.message);

      if (isMounted()) {
        toast.success(<>{t('invoice:invoiceMissionPage.rejectMissionSuccess')}</>);
        reset();
        setOpenRejectModal(false);
        refetchMission(true);
      }
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('invoice:invoiceMissionPage.rejectMissionError')}</>);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container gap={2}>
      <Grid item>
        <Button size='medium' variant='outlined' onClick={() => setConfirmApproveDialog(true)}>
          {t('invoice:invoiceMissionPage.approveSendout')}
        </Button>
      </Grid>
      <Grid item>
        <Button color='error' size='medium' variant='outlined' onClick={() => setOpenRejectModal(true)}>
          {t('invoice:invoiceMissionPage.rejectApproval')}
        </Button>
      </Grid>
      {openRejectModal && (
        <>
          <GenericDialog
            maxWidth='xs'
            open={openRejectModal}
            onClose={() => {
              reset();
              setOpenRejectModal(false);
            }}
            onSave={handleSubmit(rejectMission)}
            dialogTitle={t('invoice:invoiceMissionPage.rejectDialogTitle', { sending: setSendingName() })}
            dialogContent={
              <FormProvider {...formMethods}>
                <form>
                  <Box mx={-2} mt={-2} mb={3}>
                    <Alert severity='warning'>{t('invoice:invoiceMissionPage.rejectDialogInstruction')} </Alert>
                  </Box>
                  <ControlledTextField multiline rows={5} name='message' label={t('invoice:invoiceMissionPage.reason')} />
                </form>
              </FormProvider>
            }
          />
        </>
      )}
      <DeleteConfirmationDialog
        title={t('invoice:invoiceMissionPage.approveConfirmTitle')}
        value=''
        modaltext={t('invoice:invoiceMissionPage.approveConfirmMessage')}
        onClose={(value) => {
          if (value) {
            approveMission();
          } else {
            setConfirmApproveDialog(false);
          }
        }}
        open={confirmApproveDialog}
      />
    </Grid>
  );
};
